import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, deDE, GridColDef } from '@mui/x-data-grid';
import { Add, Close } from '@mui/icons-material';
import {
  ILegalTransactionRelationshipRealEstate,
  initRealEstate,
} from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipRealEstate/ILegalTransactionRelationshipRealEstate';
import { ILegalTransactionFullObject } from '../../Interfaces/ILegalTransaction';
import { RealEstateEditDialog } from './RealEstateEditDialog';
import { EigentuemerRechercheDialog } from '../LandRegisterQuery/EigentuemerRechercheDialog';
import { ILegalTransactionGrundbuchblattLink } from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import {
  IGrundbuchblattHit,
  useSearchGrundbuchblatt,
} from '../../hooks/reactQuerys/GrundbuchblattQueries';

interface IProps {
  open: boolean;
  onClose: () => void;
  /** Übergibt das gewählte (bestehende) oder neu erfasste Grundstück (Alt-Pfad,
   *  für die Neuanlage über den Editor). */
  onConfirm: (realEstate: ILegalTransactionRelationshipRealEstate) => void;
  /** Vorgangskontext — damit auch bei der Neuanlage die Eigentümerrecherche
   *  (Grundbuch-Tab) verfügbar ist. */
  legalTransaction?: ILegalTransactionFullObject;
  /** Ausgewählte bzw. aus der Recherche angelegte Blätter dem Vorgang zuordnen
   *  (Verknüpfung erst beim Speichern des Vorgangs). */
  onBlaetterAssigned?: (links: ILegalTransactionGrundbuchblattLink[]) => void;
}

const columns: GridColDef[] = [
  { field: 'Blattnummer', headerName: 'Blatt', flex: 1, minWidth: 90 },
  { field: 'Amtsgericht', headerName: 'Amtsgericht', flex: 1.3, minWidth: 130 },
  { field: 'Gemarkung', headerName: 'Gemarkung', flex: 1.3, minWidth: 130 },
  {
    field: 'FlurstueckAnzahl',
    headerName: 'Flurstücke',
    width: 100,
    renderCell: ({ value }) => value ?? 0,
  },
  { field: 'Eigentuemer', headerName: 'Eigentümer', flex: 2, minWidth: 160 },
  { field: 'Adressen', headerName: 'Adressen', flex: 2, minWidth: 160 },
];

/**
 * Auswahl-Dialog für Grundbuchobjekte (Stil wie „Mandanten zuordnen"): Suche +
 * „+" oben, darunter ein paginiertes DataGrid aller Grundbuchblätter. Auswahl →
 * dem Vorgang zuordnen (blatt-zentrisch, Verknüpfung beim Vorgang-Speichern).
 * Über „+" gelangt man zur Neuanlage („Blattnummer bekannt?" → Editor bzw.
 * Eigentümerrecherche).
 */
export const AssignRealEstateDialog: React.FC<IProps> = ({
  open,
  onClose,
  onConfirm,
  legalTransaction,
  onBlaetterAssigned,
}) => {
  const [mode, setMode] = useState<'list' | 'ask' | 'recherche' | 'create'>(
    'list'
  );
  // Ergebnis der Eigentümerrecherche (vorausgefülltes Grundstück für den Editor).
  const [rechercheResult, setRechercheResult] =
    useState<ILegalTransactionRelationshipRealEstate | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);
  const { grundbuchblattHits, isSearchingGrundbuchblatt } =
    useSearchGrundbuchblatt(debounced);

  const [rowSelectionModel, setRowSelectionModel] = useState<number[]>([]);
  // Cache aller je gesehenen Treffer, damit die Auswahl über Suchänderungen /
  // Seiten hinweg erhalten bleibt.
  const [seen, setSeen] = useState<Map<number, IGrundbuchblattHit>>(new Map());
  useEffect(() => {
    if (!grundbuchblattHits.length) return;
    setSeen(prev => {
      const next = new Map(prev);
      grundbuchblattHits.forEach(h => next.set(h.idGrundbuchblatt, h));
      return next;
    });
  }, [grundbuchblattHits]);

  const [newRealEstate] = useState(initRealEstate());

  useEffect(() => {
    if (open) {
      setMode('list');
      setSearchInput('');
      setDebounced('');
      setRowSelectionModel([]);
      setRechercheResult(null);
    }
  }, [open]);

  const confirmSelectedBlaetter = () => {
    const links: ILegalTransactionGrundbuchblattLink[] = rowSelectionModel
      .map(id => seen.get(id))
      .filter((h): h is IGrundbuchblattHit => !!h)
      .map(h => ({
        idGrundbuchblatt: h.idGrundbuchblatt,
        Blattnummer: h.Blattnummer,
        idCourt: h.idCourt ?? null,
        idAdministrativeUnit: h.idAdministrativeUnit ?? null,
        Amtsgericht: h.Amtsgericht ?? null,
        Gemarkung: h.Gemarkung ?? null,
        FlurstueckAnzahl: h.FlurstueckAnzahl ?? null,
      }));
    if (links.length > 0) onBlaetterAssigned?.(links);
    onClose();
  };

  // Neuanlage Schritt 1: Ist die Blattnummer bekannt?
  if (open && mode === 'ask') {
    return (
      <Dialog open onClose={onClose} maxWidth='xs' fullWidth>
        <DialogTitle>Neues Grundbuchblatt</DialogTitle>
        <DialogContent dividers>
          <Typography variant='body2'>Ist die Blattnummer bekannt?</Typography>
          <Typography variant='caption' color='text.secondary'>
            Wenn nicht, ermitteln Sie Blatt, Flur/Flurstück und Eigentümer über
            die Eigentümerrecherche im Grundbuch.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button onClick={() => setMode('list')}>Zurück</Button>
          <Box>
            <Button onClick={() => setMode('recherche')}>
              Nein – Eigentümerrecherche
            </Button>
            <Button
              variant='contained'
              onClick={() => setMode('create')}
              sx={{ ml: 1 }}
            >
              Ja
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    );
  }

  // Neuanlage Schritt 2a (Blattnummer unbekannt): Eigentümerrecherche.
  if (open && mode === 'recherche') {
    return (
      <EigentuemerRechercheDialog
        realEstateObject={newRealEstate}
        setRealEstateObject={(
          updated: ILegalTransactionRelationshipRealEstate
        ) => {
          setRechercheResult(updated);
          setMode('create');
        }}
        legalTransaction={legalTransaction}
        autoOpen
        hideTrigger
        onClosed={() => setMode('ask')}
        onBlaetterAssigned={
          onBlaetterAssigned
            ? links => {
                onBlaetterAssigned(links);
                onClose();
              }
            : undefined
        }
      />
    );
  }

  // Neuanlage Schritt 2b: Blatt-Editor (ggf. aus Recherche-Ergebnis vorbelegt).
  if (open && mode === 'create') {
    return (
      <RealEstateEditDialog
        realEstate={rechercheResult ?? newRealEstate}
        onSave={re => onConfirm(re)}
        onBlattSaved={(idGrundbuchblatt, Blattnummer) =>
          onBlaetterAssigned?.([{ idGrundbuchblatt, Blattnummer }])
        }
        onClose={() => setMode('list')}
        legalTransaction={legalTransaction}
      />
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='lg' fullWidth>
      <DialogTitle>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
        >
          <Typography variant='h6'>Grundstück hinzufügen</Typography>
          <IconButton size='small' onClick={onClose}>
            <Close fontSize='small' />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ display: 'flex', flexDirection: 'column', height: 640 }}
      >
        <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
          Suchen Sie zuerst, ob das Grundbuchblatt bereits in DiNo hinterlegt
          ist (Blattnummer, Amtsgericht, Gemarkung, Flur, Flurstück, Adresse
          oder Eigentümer) — so vermeiden Sie Doppel-Einträge. Ist es noch nicht
          vorhanden, legen Sie über „+" ein neues an.
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            autoFocus
            fullWidth
            size='small'
            placeholder='Blattnummer, Amtsgericht, Gemarkung, Flur, Flurstück, Adresse oder Eigentümer …'
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
          />
          <Tooltip title='Neues Grundbuchblatt anlegen'>
            <IconButton color='primary' onClick={() => setMode('ask')}>
              <Add />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0 }}>
          <DataGrid
            sx={{ width: '100%', height: '100%' }}
            loading={isSearchingGrundbuchblatt}
            rows={grundbuchblattHits}
            columns={columns}
            getRowId={row => row.idGrundbuchblatt}
            checkboxSelection
            pagination
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={newModel =>
              setRowSelectionModel(newModel.map(Number))
            }
            keepNonExistentRowsSelected
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant='outlined' onClick={onClose}>
          Abbruch
        </Button>
        <Button
          variant='contained'
          disabled={rowSelectionModel.length === 0}
          onClick={confirmSelectedBlaetter}
        >
          Übernehmen ({rowSelectionModel.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};
